'use client';
import { useMemo, useState } from 'react';
import { ChevronDown, Plus, Trash, X } from 'lucide-react';
import { twMerge } from 'tailwind-merge';
import {
  MESSAGE_POLL_DURATION,
  MESSAGE_POLL_DURATION_CHOICES,
  MESSAGE_POLL_LIMITS,
  isPollDurationAtMaxDays,
  pollDurationParts,
  setPollDurationPart,
  validateMessagePollDraft,
  type IMessagePollDraft,
  type TPollDurationPart,
} from '@polls/message-poll';

let optionKeyCounter = 0;
const charCount = (text: string) => Array.from(text).length;
const nextOptionKey = () => `poll-option-${(optionKeyCounter += 1)}`;

export const createEmptyPollDraft = (): IMessagePollDraft => ({
  question: '',
  options: [
    { text: '', key: nextOptionKey() },
    { text: '', key: nextOptionKey() },
  ],
  duration_minutes: MESSAGE_POLL_DURATION.defaultMinutes,
});

export interface IPollDraftServerError {
  field: string;
  error: string;
}

interface MessagePollDraftEditorProps {
  draft: IMessagePollDraft;
  onChange: (draft: IMessagePollDraft) => void;
  onRemove: () => void;
  readOnly?: boolean;
  serverError?: IPollDraftServerError | null;
  className?: string;
}

const serverFieldOf = (field?: string) => {
  if (!field) return null;
  if (field.startsWith('options')) return 'options';
  if (field.startsWith('question')) return 'question';
  if (field.startsWith('duration_minutes') || field.startsWith('duration_hours')) {
    return 'duration_minutes';
  }
  return null;
};

const inputClass =
  'w-full rounded-sm border border-primaryLight bg-white p-2 pl-4 pr-14 text-[14px] disabled:cursor-not-allowed read-only:bg-gray-50';

const DURATION_PARTS: { part: TPollDurationPart; label: string }[] = [
  { part: 'days', label: 'Days' },
  { part: 'hours', label: 'Hours' },
  { part: 'minutes', label: 'Minutes' },
];

const Hint = ({ id, children }: { id: string; children?: string }) =>
  children ? (
    <p id={id} role="alert" className="mt-1 text-xs text-red-600">
      {children}
    </p>
  ) : null;

export const MessagePollDraftEditor = ({
  draft,
  onChange,
  onRemove,
  readOnly = false,
  serverError,
  className,
}: MessagePollDraftEditorProps) => {
  const [touched, setTouched] = useState({ question: false, options: false });
  const errors = useMemo(() => validateMessagePollDraft(draft), [draft]);
  const serverField = serverFieldOf(serverError?.field);

  const showQuestionHint = touched.question || draft.question.trim().length > 0;
  const showOptionsHint = touched.options || draft.options.some((o) => o.text.trim().length > 0);

  const questionHint =
    serverField === 'question'
      ? serverError?.error
      : showQuestionHint
      ? errors.question
      : undefined;
  const optionsHint =
    serverField === 'options' ? serverError?.error : showOptionsHint ? errors.options : undefined;
  const durationHint =
    serverField === 'duration_minutes' ? serverError?.error : errors.duration_minutes;
  const durationParts = pollDurationParts(draft.duration_minutes);
  const durationAtMax = isPollDurationAtMaxDays(draft.duration_minutes);
  const generalHint = serverError && !serverField ? serverError.error : undefined;

  const canAdd = draft.options.length < MESSAGE_POLL_LIMITS.optionsMax;
  const canRemove = draft.options.length > MESSAGE_POLL_LIMITS.optionsMin;

  const setOptionText = (index: number, text: string) => {
    setTouched((state) => ({ ...state, options: true }));
    onChange({
      ...draft,
      options: draft.options.map((option, i) => (i === index ? { ...option, text } : option)),
    });
  };

  return (
    <div
      data-testid="message-poll-draft-editor"
      className={twMerge('flex flex-col gap-3 rounded border border-primaryHover p-3', className)}>
      <div className="flex items-center justify-between">
        <p className="text-sm font-semibold">Poll</p>
        <button
          type="button"
          disabled={readOnly}
          onClick={onRemove}
          className="flex items-center gap-1 text-xs text-red-600 hover:underline disabled:cursor-not-allowed disabled:opacity-50">
          <Trash className="h-3 w-3" />
          Remove poll
        </button>
      </div>

      <div>
        <div className="relative">
          <input
            type="text"
            aria-label="Poll question"
            aria-invalid={Boolean(questionHint)}
            aria-describedby={questionHint ? 'message-poll-question-hint' : undefined}
            placeholder="Ask a question"
            value={draft.question}
            readOnly={readOnly}
            onBlur={() => setTouched((state) => ({ ...state, question: true }))}
            onChange={(event) => {
              setTouched((state) => ({ ...state, question: true }));
              onChange({ ...draft, question: event.target.value });
            }}
            className={twMerge(inputClass, 'h-[48px] font-bold')}
          />
          <span className="absolute bottom-1 right-2 text-xs text-gray-500">
            {charCount(draft.question)}/{MESSAGE_POLL_LIMITS.questionMax}
          </span>
        </div>
        <Hint id="message-poll-question-hint">{questionHint}</Hint>
      </div>

      <div className="flex flex-col gap-2">
        {draft.options.map((option, index) => (
          <div key={option.key ?? index} className="flex items-center gap-2">
            <div className="relative flex-1">
              <input
                type="text"
                aria-label={`Poll option ${index + 1}`}
                aria-invalid={Boolean(optionsHint)}
                placeholder={`Option ${index + 1}`}
                value={option.text}
                readOnly={readOnly}
                onBlur={() => setTouched((state) => ({ ...state, options: true }))}
                onChange={(event) => setOptionText(index, event.target.value)}
                className={twMerge(inputClass, 'h-[44px]')}
              />
              <span className="absolute bottom-1 right-2 text-xs text-gray-500">
                {charCount(option.text)}/{MESSAGE_POLL_LIMITS.optionTextMax}
              </span>
            </div>
            {canRemove && (
              <button
                type="button"
                aria-label={`Remove option ${index + 1}`}
                disabled={readOnly}
                onClick={() =>
                  onChange({ ...draft, options: draft.options.filter((_, i) => i !== index) })
                }
                className="rounded p-1 text-primary hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        ))}
        <Hint id="message-poll-options-hint">{optionsHint}</Hint>
        {canAdd && (
          <button
            type="button"
            disabled={readOnly}
            onClick={() =>
              onChange({
                ...draft,
                options: [...draft.options, { text: '', key: nextOptionKey() }],
              })
            }
            className="flex w-fit items-center gap-1 rounded-md border border-primaryActive px-3 py-1 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50">
            <Plus className="h-4 w-4" />
            Add option
          </button>
        )}
      </div>

      <div>
        <p id="message-poll-duration-label" className="mb-1 text-xs text-gray-600">
          Poll length
        </p>
        <div role="group" aria-labelledby="message-poll-duration-label" className="flex gap-2">
          {DURATION_PARTS.map(({ part, label }) => (
            <div
              key={part}
              className="relative flex-1 rounded-sm border border-primaryLight bg-white px-3 pb-1 pt-1.5 has-[:disabled]:opacity-50">
              <p aria-hidden="true" className="text-xs text-gray-600">
                {label}
              </p>
              <select
                aria-label={label}
                aria-describedby={durationHint ? 'message-poll-duration-hint' : undefined}
                data-testid={`message-poll-duration-${part}`}
                value={durationParts[part]}
                disabled={readOnly || (part !== 'days' && durationAtMax)}
                onChange={(event) =>
                  onChange({
                    ...draft,
                    duration_minutes: setPollDurationPart(
                      draft.duration_minutes,
                      part,
                      Number(event.target.value),
                    ),
                  })
                }
                className="w-full appearance-none bg-transparent pr-6 text-[14px] focus:outline-none disabled:cursor-not-allowed">
                {MESSAGE_POLL_DURATION_CHOICES[part].map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
              <ChevronDown
                aria-hidden="true"
                className="pointer-events-none absolute bottom-2 right-3 h-4 w-4 text-gray-600"
              />
            </div>
          ))}
        </div>
        <Hint id="message-poll-duration-hint">{durationHint}</Hint>
      </div>

      <Hint id="message-poll-general-hint">{generalHint}</Hint>
    </div>
  );
};
